import { captureException } from '@chilipiper/sentry'
import { formatInTimeZone } from '../date-timezone'
import { timezonesByArea } from '../data/timezonesByArea'
import { timezoneAbbreviations } from '../data/timezoneAbbreviations'
import { timezonesCities } from '../data/timezonesCities'
import { timezoneCouldFail } from './helpers'

export const timezones = new Set(Object.values(timezonesByArea).flat())
export type TimezoneId = keyof typeof timezonesCities

export const getAllTimezoneIdentifiersByArea = () => {
  return timezonesByArea
}

/**
 * Returns the main city for the given timezone identifier and undefined if timezone is not
 * supported
 */
export const getTimezoneCities = (timezoneIdentifier: string): readonly string[] | undefined => {
  return timezonesCities[timezoneIdentifier as TimezoneId]
}

/**
 * Returns the timezone data for the given timezone identifier, if the identifier is not known to
 * the browser returns undefined
 */
export const getTimezoneData = (timezoneIdentifier: string, time?: Date) => {
  const date = time ?? new Date()
  try {
    const [offset, ...timezoneParts] = formatInTimeZone(date, timezoneIdentifier, 'xxxxx zzzz', {
      locale: 'en-US',
    }).split(' ')
    if (!offset) {
      return undefined
    }
    let timezone = timezoneParts.join(' ')
    const abbreviation = getTimezoneAbbreviation(timezone)
    if (timezone.includes('GMT')) {
      timezone = timezoneIdentifier
    } else if (!abbreviation) {
      const message = `Timezone abbreviation not found for ${timezone}`
      console.warn(message)
      captureException(new Error(message))
    }

    return {
      name: timezone,
      offset: offset,
      abbreviation: abbreviation ?? `GMT${offset}`,
    }
  } catch (e) {
    const message = `Timezone data not found for ${timezoneIdentifier}`
    console.warn(message)
    if (!timezoneCouldFail(timezoneIdentifier)) {
      captureException(new Error(message, { cause: e }))
    }
    return undefined
  }
}

type CommonTimezoneName = keyof typeof timezoneAbbreviations

/** Returns the timezone abbreviation for the given timezone name or undefined if nothing matched */
const getTimezoneAbbreviation = (commonTimezoneName: string): string | undefined => {
  if (timezoneAbbreviations[commonTimezoneName as CommonTimezoneName]) {
    return timezoneAbbreviations[commonTimezoneName as CommonTimezoneName]
  }
  // Because of discrepancies between IANA timezone names and the ones used by the browser we need to provide multiple fallbacks
  const fallback = fallbacks.find(([from, to]) => {
    return timezoneAbbreviations[commonTimezoneName.replace(from, to) as CommonTimezoneName]
  })
  if (fallback) {
    return timezoneAbbreviations[
      commonTimezoneName.replace(fallback[0], fallback[1]) as CommonTimezoneName
    ]
  }
}

const fallbacks = [
  // for example Korean Standard Time -> Korean Time fallback
  [' Standard', ''],
  // East Greenland Summer Time -> Eastern Greenland Time fallback
  ['East ', 'Eastern '],
  // West Greenland Summer Time -> Western Greenland Time fallback
  ['West ', 'Western '],
  // St. Pierre & Miquelon Daylight Time -> Pierre & Miquelon Daylight Time fallback
  ['St. ', ''],
  // Pitcairn Time -> Pitcairn Standard Time fallback
  ['Time', 'Standard Time'],
  // America/Hermosillo, why?? Mexican Pacific Standard Time -> Mountain Standard Time
  ['Mexican Pacific ', 'Mountain '],
  // Chatham Daylight Time -> Chatham Island Daylight Time
  ['Chatham ', 'Chatham Island '],
  // Norfolk Island Daylight Time -> Norfolk Daylight Time fallback
  ['Norfolk Island ', 'Norfolk '],
  // Timor-Leste Time -> East Timor Time fallback
  ['Timor-Leste ', 'East Timor '],
  // Dumont d'Urville Time -> Dumont-d'Urville Time fallback
  ["Dumont d'Urville ", "Dumont-d'Urville "],
  // Taiwan Standard Time -> China Standard Time fallback
  ['Taiwan ', 'China '],
  // American Samoa Standard Time -> Samoa Standard Time fallback
  ['American Samoa ', 'Samoa '],
  // Brunei Time -> Brunei Darussalam Time fallback
  ['Brunei ', 'Brunei Darussalam '],
  // Greenland Summer Time -> Western Greenland Summer Time fallback
  ['Greenland ', 'Western Greenland '],
  // Greenland Standard Time -> West Greenland Time fallback
  ['Greenland Standard ', 'West Greenland '],
]
